import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

/**
 * Landing page Square POS app redirects to after a tap-to-pay transaction.
 * Reads the result from URL params, POSTs to backend to mark the order
 * paid (or failed), and shows a status screen.
 *
 * Square POS callback URL params:
 *   ?order_id=<our order id>
 *   &com.squareup.pos.SERVER_TRANSACTION_ID=<square txn id>
 *   &com.squareup.pos.ERROR_CODE=<if failed>
 *   &com.squareup.pos.ERROR_DESCRIPTION=<if failed>
 *   &com.squareup.pos.CLIENT_TRANSACTION_ID=<...>
 */
export default function PosSquareCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing | paid | failed
  const [error, setError] = useState(null);

  useEffect(() => {
    const orderId = params.get("order_id");
    const txnId =
      params.get("com.squareup.pos.SERVER_TRANSACTION_ID") ||
      params.get("transaction_id");
    const errCode = params.get("com.squareup.pos.ERROR_CODE");
    const errDesc = params.get("com.squareup.pos.ERROR_DESCRIPTION");

    if (!orderId) {
      // Defer setState to next tick so React doesn't warn about synchronous
      // state updates inside an effect.
      Promise.resolve().then(() => {
        setStatus("failed");
        setError("Missing order ID in callback URL");
      });
      return;
    }
    const qs = new URLSearchParams({ order_id: orderId });
    if (txnId) qs.append("transaction_id", txnId);
    if (errCode) qs.append("error_code", errCode);
    if (errDesc) qs.append("error_description", errDesc);
    api
      .post(`/pos/card/confirm?${qs.toString()}`)
      .then((r) => {
        if (r.data.payment_status === "paid") {
          setStatus("paid");
        } else {
          setStatus("failed");
          setError(errDesc || "Payment was not completed");
        }
      })
      .catch((e) => {
        setStatus("failed");
        setError(e.response?.data?.detail || "Couldn't confirm payment");
      });
  }, [params]);

  return (
    <div
      className="min-h-screen bg-gray-50 p-4 flex items-center justify-center"
      data-testid="pos-square-callback"
    >
      <div className="bg-white max-w-md w-full p-10 border-2 border-[#0A1C40] text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto animate-spin text-[#0A1C40]" />
            <h2 className="font-display font-black text-2xl mt-4">
              Confirming payment…
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Talking to Square. Don&apos;t close this window.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-20 h-20 mx-auto text-green-600" strokeWidth={2.5} />
            <h2 className="font-display font-black text-3xl mt-3">Paid!</h2>
            <p className="text-sm text-gray-600 mt-2">
              Customer&apos;s card was charged successfully.
            </p>
            <button
              onClick={() => navigate("/pos", { replace: true })}
              className="mt-8 w-full bg-[#0A1C40] text-white font-bold py-3 rounded-full hover:bg-black transition-colors"
              data-testid="pos-callback-return-btn"
            >
              Return to POS →
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-20 h-20 mx-auto text-[#DC2626]" strokeWidth={2.5} />
            <h2 className="font-display font-black text-3xl mt-3">Payment failed</h2>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
            <button
              onClick={() => navigate("/pos", { replace: true })}
              className="mt-8 w-full bg-[#0A1C40] text-white font-bold py-3 rounded-full hover:bg-black transition-colors"
            >
              Back to POS — try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
